// js/app.js
import { Router } from "./router.js";
import { API_BASE_URL, TEMPLATES_BASE_URL } from "./api.js";
import { DraftSiteRuntime, PublicSiteRuntime } from "./public-site.js";

const SIDEBAR_STATE_KEY = "heyweb_sidebar_collapsed";
const App = {
    editorPreviousSidebarCollapsed: null,

    async init() {
        if (DraftSiteRuntime.isDraftPath()) {
            await DraftSiteRuntime.mount();
            return;
        }

        if (PublicSiteRuntime.isPublicPath()) {
            await PublicSiteRuntime.mount();
            return;
        }

        await Router.init();

        window.addEventListener("hashchange", () => {
            setTimeout(() => this.handleRouteChange(), 0);
        });

        this.bindGlobalActions();
        this.handleRouteChange();

        console.log(
            "%c HeyWeb Engine %c SPA inicializada ",
            "background: #3b82f6; color: white; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
            "background: #1e293b; color: #3b82f6; font-weight: bold; padding: 2px 5px; border-radius: 3px;"
        );

        console.log(`Backend API: ${API_BASE_URL}`);
        console.log(`Templates (Eleventy): ${TEMPLATES_BASE_URL}`);
    },

    bindGlobalActions() {
        document.addEventListener("click", (event) => {
            const logoutTrigger = event.target.closest("[data-action='logout']");
            if (!logoutTrigger) return;

            localStorage.removeItem("heyweb_token");
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_email");
            this.setProfilePanelOpen(false);
            window.location.hash = "";
        });

        document.addEventListener("click", (event) => {
            const toggleButton = event.target.closest("#sidebar-toggle");
            if (!toggleButton) return;

            const isCollapsed = document.body.classList.toggle("sidebar-collapsed");
            localStorage.setItem(SIDEBAR_STATE_KEY, isCollapsed ? "1" : "0");
            this.updateSidebarToggle();
        });

        document.addEventListener("click", (event) => {
            const profileToggle = event.target.closest("[data-action='toggle-profile-panel']");
            if (!profileToggle) return;

            const nextState = !document.body.classList.contains("profile-panel-open");
            this.setProfilePanelOpen(nextState);
        });

        document.addEventListener("click", (event) => {
            const closeProfile = event.target.closest("#profile-panel-close, #profile-panel-backdrop");
            if (!closeProfile) return;
            this.setProfilePanelOpen(false);
        });

        document.addEventListener("click", (event) => {
            if (!document.body.classList.contains("profile-panel-open")) return;
            if (event.target.closest("#profile-panel")) return;
            if (event.target.closest("[data-action='toggle-profile-panel']")) return;
            this.setProfilePanelOpen(false);
        });

        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                this.setProfilePanelOpen(false);
            }
        });
    },

    handleRouteChange() {
        this.updateLayout();
        this.reinitUI();
    },

    updateLayout() {
        const token = localStorage.getItem("heyweb_token");
        const hash = window.location.hash || "#";
        const rawBaseHash = hash.split("/")[0] || "#";
        const baseHash = rawBaseHash === "#" ? "#templates" : rawBaseHash;

        const body = document.body;
        const sidebar = document.getElementById("sidebar");
        const headerActions = document.getElementById("header-actions");
        const sidebarLinks = document.querySelectorAll("#main-nav .nav-link");
        const sidebarUserName = document.getElementById("sidebar-user-name");
        const profilePanelUserName = document.getElementById("profile-panel-user-name");
        const profilePanelUserEmail = document.getElementById("profile-panel-user-email");
        const userEmail = localStorage.getItem("user_email") || "";
        const storedUserName =
            localStorage.getItem("user_name") ||
            localStorage.getItem("name") ||
            localStorage.getItem("user") ||
            "";
        const fallbackNameFromEmail = userEmail.includes("@")
            ? userEmail.split("@")[0].replace(/[._-]+/g, " ").trim()
            : "";
        const resolvedUserName = storedUserName || fallbackNameFromEmail || "Utilizador";
        const prefersCollapsedSidebar = localStorage.getItem(SIDEBAR_STATE_KEY) === "1";
        const isEditorRoute = hash.startsWith("#editor/");
        const shouldExpandEditor = Boolean(token) && isEditorRoute;

        body.classList.toggle("is-authenticated", Boolean(token));
        body.classList.toggle("is-public-route", !token);
        body.classList.toggle("sidebar-collapsed", Boolean(token) && prefersCollapsedSidebar);
        body.classList.toggle("editor-expanded", Boolean(token) && shouldExpandEditor);

        if (sidebar) {
            sidebar.hidden = !token;
        }

        if (!isEditorRoute || !token) {
            this.editorPreviousSidebarCollapsed = null;
            body.classList.remove("editor-expanded");
            body.classList.toggle("sidebar-collapsed", Boolean(token) && prefersCollapsedSidebar);
        } else {
            if (this.editorPreviousSidebarCollapsed === null) {
                this.editorPreviousSidebarCollapsed = prefersCollapsedSidebar;
            }
            body.classList.add("sidebar-collapsed");
        }

        if (sidebarUserName) {
            sidebarUserName.innerText = token ? resolvedUserName : "Workspace ativo";
        }

        if (profilePanelUserName) {
            profilePanelUserName.innerText = token ? resolvedUserName : "Utilizador";
        }

        if (profilePanelUserEmail) {
            profilePanelUserEmail.innerText = token ? (userEmail || "Sem e-mail") : "Sem e-mail";
        }

        if (headerActions) {
            headerActions.innerHTML = token
                ? `
                    <a href="#dashboard" class="ui-icon-button" aria-label="Dashboard" title="Dashboard">
                        <i data-lucide="house"></i>
                    </a>
                    <button type="button" class="ui-icon-button ui-icon-button--dark" data-action="toggle-profile-panel" aria-label="Perfil" title="Perfil">
                        <i data-lucide="user"></i>
                    </button>
                  `
                : `
                    <a href="#login" class="header-cta header-cta-secondary">Entrar</a>
                    <a href="#register" class="header-cta header-cta-primary">Criar Conta</a>
                  `;
        }

        if (!token) {
            this.setProfilePanelOpen(false);
        }

        [...sidebarLinks].forEach((link) => {
            const href = link.getAttribute("href") || "";
            const isActive = href === baseHash;
            link.classList.toggle("active", isActive);
        });

        this.updateSidebarToggle();
    },

    setProfilePanelOpen(open) {
        const isOpen = Boolean(open);
        const backdrop = document.getElementById("profile-panel-backdrop");
        const panel = document.getElementById("profile-panel");

        document.body.classList.toggle("profile-panel-open", isOpen);

        if (backdrop) {
            backdrop.hidden = !isOpen;
        }

        if (panel) {
            panel.setAttribute("aria-hidden", isOpen ? "false" : "true");
        }

        if (isOpen && window.lucide) {
            window.lucide.createIcons();
        }
    },

    updateSidebarToggle() {
        const toggleButton = document.getElementById("sidebar-toggle");
        if (!toggleButton) return;

        const isCollapsed = document.body.classList.contains("sidebar-collapsed");
        toggleButton.setAttribute("aria-label", isCollapsed ? "Expandir sidebar" : "Recolher sidebar");
        toggleButton.setAttribute("title", isCollapsed ? "Expandir sidebar" : "Recolher sidebar");
        toggleButton.innerHTML = isCollapsed
            ? `<i data-lucide="chevrons-right"></i>`
            : `<i data-lucide="chevrons-left"></i>`;

        if (window.lucide) window.lucide.createIcons();
    },
    reinitUI() {
        setTimeout(() => {
            if (window.lucide) window.lucide.createIcons();
            if (!document.body.classList.contains("editor-expanded")) {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        }, 50);
    },
};

document.addEventListener("DOMContentLoaded", () => {
    App.init();
});
